import { useRef } from 'react';
import { Box, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { useColorModeValue } from '../../components/ui/color-mode';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputBox } from '../../components/shared/fields';
import ButtonBox from '../../components/shared/buttons/ButtonBox';
import { useDispatch, useSelector } from 'react-redux';
import { showAlert } from '../../store/alertSlice';
import { updateUserAttributesRequest } from '../../store/userSlice';
import { darkMode, lightMode } from '../../theme/config';

const redStar = <span style={{ color: "red" }}>*</span>;

const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First Name is required')
    .max(30, 'First Name cannot exceed 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last Name is required')
    .max(30, 'Last Name cannot exceed 50 characters'),
});

const ProfileTab = () => {
  const textColor = useColorModeValue(lightMode.textColor, darkMode.textColor);
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user) || {};
  const { email, firstName, lastName, phoneNumber } = user;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: firstName || '',
      lastName: lastName || '',
    },
  });

  const initialValues = useRef({
    firstName: firstName || '',
    lastName: lastName || '',
  });

  const onSubmit = (data) => {
    // console.log(data)
    const noChange =
      data.firstName === initialValues.current.firstName &&
      data.lastName === initialValues.current.lastName;
    // console.log(noChange)
    if (noChange) {
      dispatch(
        showAlert({
          message:
            'No changes detected. Please modify your name before saving.',
          type: 'error',
        })
      );
      return;
    }
    try {
      dispatch(updateUserAttributesRequest({
        email,                   // grab from Redux
        firstName: data.firstName,
        lastName: data.lastName,
        platform: "crm"
      }));
    } catch (err) {
      // console.log(err)
      dispatch(showAlert({ message: "Error updating profile", type: "error" }));
    }
  };

  return (
    <Box p={4} color={textColor}>
      <Text fontSize="lg" fontWeight="bold" mb={4} color={textColor}>
        Profile Information
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }} gap={6} mb={4}>
          <GridItem>
            <InputBox
              label={<>{"First Name"} {redStar}</>}
              placeholder="Enter your first name"
              register={register('firstName')}
              invalid={!!errors.firstName}
              errorText={errors.firstName?.message}
            />
          </GridItem>

          <GridItem>
            <InputBox
              label={<>{"Last Name"} {redStar}</>}
              placeholder="Enter your last name"
              register={register('lastName')}
              invalid={!!errors.lastName}
              errorText={errors.lastName?.message}
            />
          </GridItem>
        </Grid>

        <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }} gap={6} mb={6}>
          <GridItem>
            <InputBox
              label="Email Address"
              placeholder={email}
              register={register('email')}
              disabled={true}
            />
          </GridItem>
          <GridItem>
            <InputBox
              label="Phone Number"
              placeholder={phoneNumber || ''}
              register={register('phoneNumber')}
              disabled={true}
            />
          </GridItem>
        </Grid>
        <Flex justify="flex-end">
          <ButtonBox
            name="Save Changes"
            variant="solid"
            colorPalette="blue"
            borderRadius={12}
            type="submit"
          />
        </Flex>
      </form>
    </Box>
  );
};

export default ProfileTab;